import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, Award, AlertCircle, DollarSign, Gift, Copy, Check, Loader2, Cake, Star, Car, Mail
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

import { useAuth } from "../context";
import { API, TIME_SLOTS, getStatusColor, formatStatus } from "../lib/constants";
import BookingModal from "../components/booking/BookingModal";
import DiscountPopup from "../components/booking/DiscountPopup";
import { Header, Footer } from "../components/common";

const AccountPage = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(true); // true = login, false = register
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    birthday: ""
  });
  const [error, setError] = useState("");
  const { login, register } = useAuth();
  
  // Booking management state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleDetails, setRescheduleDetails] = useState({
    address: "",
    phone: "",
    special_instructions: ""
  });

  const reschedule_times = TIME_SLOTS;

  const loadBookings = async () => {
    if (user) {
      try {
        const token = localStorage.getItem('rainxpress_token');
        const [bookingsRes, loyaltyRes] = await Promise.all([
          axios.get(`${API}/auth/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/auth/loyalty`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setBookings(bookingsRes.data.bookings);
        setLoyalty(loyaltyRes.data);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    loadBookings();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (showLogin) {
        await login(formData.email, formData.password);
      } else {
        await register(formData.name, formData.email, formData.password, formData.phone, formData.birthday);
      }
      toast.success(showLogin ? "Welcome back!" : "Account created successfully!");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Please enter your email address");
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("Password reset link sent!");
    } catch (error) {
      toast.error("Failed to send reset link. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleManageBooking = (booking) => {
    setSelectedBooking(booking);
    setRescheduleDetails({
      address: booking.address || "",
      phone: booking.phone || "",
      special_instructions: booking.special_instructions || ""
    });
    setShowManageModal(true);
  };

  // Handle balance payment
  const handlePayBalance = async (booking) => {
    try {
      const response = await axios.post(`${API}/pay-balance/${booking.id}`);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment session");
    }
  };

  // Calculate balance due for a booking
  const getBalanceDue = (booking) => {
    const totalPrice = booking.total_price || 0;
    const payments = booking.payments || booking.payment_records || [];
    const totalPaid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
    return Math.max(0, totalPrice - totalPaid);
  };

  const handleCancelBooking = async () => {
    try {
      const token = localStorage.getItem('rainxpress_token');
      await axios.put(`${API}/bookings/${selectedBooking.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Booking cancelled");
      setShowCancelConfirm(false);
      setShowManageModal(false);
      setSelectedBooking(null);
      loadBookings();
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      toast.error("Please select a new date and time");
      return;
    }
    try {
      const token = localStorage.getItem('rainxpress_token');
      await axios.put(`${API}/bookings/${selectedBooking.id}/reschedule`, {
        preferred_date: format(rescheduleDate, "yyyy-MM-dd"),
        preferred_time: rescheduleTime,
        address: rescheduleDetails.address,
        phone: rescheduleDetails.phone,
        special_instructions: rescheduleDetails.special_instructions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Reschedule request submitted! We'll notify you when confirmed.");
      setShowReschedule(false);
      setShowManageModal(false);
      setSelectedBooking(null);
      setRescheduleDate(null);
      setRescheduleTime("");
      loadBookings();
    } catch (error) {
      toast.error("Failed to reschedule booking");
    }
  };

  const handlePayDeposit = async (booking) => {
    try {
      const response = await axios.post(`${API}/bookings/${booking.id}/pay-deposit`, {
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.assign(response.data.checkout_url);
      }
    } catch (error) {
      toast.error("Failed to initiate payment");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in - show login/register form
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-950" data-testid="account-page">
        <Header />
        
        <section className="pt-32 pb-20 px-4 md:px-8">
          <div className="max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-8"
            >
              <h1 className="text-2xl font-bold text-center mb-6">
                {showLogin ? "Welcome Back" : "Create Account"}
              </h1>
              
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
                {!showLogin && (
                  <div>
                    <Label className="text-sm mb-2 block">Full Name</Label>
                    <Input
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      required
                      data-testid="auth-name-input"
                    />
                  </div>
                )}
                
                <div>
                  <Label className="text-sm mb-2 block">Email</Label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                    required
                    data-testid="auth-email-input"
                  />
                </div>
                
                <div>
                  <Label className="text-sm mb-2 block">Password</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                    required
                    data-testid="auth-password-input"
                  />
                </div>
                
                {!showLogin && (
                  <div>
                    <Label className="text-sm mb-2 block">Phone (optional)</Label>
                    <Input
                      placeholder="0400 000 000"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      data-testid="auth-phone-input"
                    />
                  </div>
                )}

                {!showLogin && (
                  <div>
                    <Label className="text-sm mb-2 block">Birthday (optional - for special rewards!)</Label>
                    <Input
                      type="text"
                      placeholder="MM-DD (e.g., 03-15)"
                      value={formData.birthday}
                      onChange={(e) => setFormData({...formData, birthday: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      data-testid="auth-birthday-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Get a special discount on your birthday!</p>
                  </div>
                )}

                <Button type="submit" className="w-full btn-primary-glow text-slate-900 py-3 rounded-full" data-testid="auth-submit-btn">
                  {showLogin ? "Sign In" : "Create Account"}
                </Button>
                
                {showLogin && (
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setForgotEmail(formData.email); setForgotSent(false); }}
                    className="w-full text-cyan-400 hover:text-cyan-300 text-sm mt-2"
                    data-testid="forgot-password-btn"
                  >
                    Forgot your password?
                  </button>
                )}
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => { setShowLogin(!showLogin); setError(""); }}
                  className="text-cyan-400 hover:text-cyan-300 text-sm"
                  data-testid="auth-toggle-btn"
                >
                  {showLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Forgot Password Modal */}
        <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-md" data-testid="forgot-password-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Reset Password</DialogTitle>
              <DialogDescription className="text-slate-400">
                Enter your email and we'll send you a link to reset your password.
              </DialogDescription>
            </DialogHeader>
            
            {!forgotSent ? (
              <form onSubmit={handleForgotPassword} className="space-y-4 py-4">
                <div>
                  <Label className="text-sm mb-2 block">Email Address</Label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="bg-slate-800 border-slate-700"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowForgotPassword(false)}
                    className="flex-1 border-slate-600"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={forgotLoading}
                    className="flex-1 btn-primary-glow text-slate-900"
                    data-testid="forgot-submit-btn"
                  >
                    {forgotLoading ? "Sending..." : "Send Reset Link"}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="py-6 text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Check Your Email</h3>
                <p className="text-slate-400 text-sm mb-4">
                  We've sent a password reset link to <span className="text-cyan-400">{forgotEmail}</span>
                </p>
                <p className="text-slate-500 text-xs mb-4">
                  Didn't receive it? Check your spam folder or try again.
                </p>
                <Button 
                  onClick={() => setShowForgotPassword(false)}
                  className="btn-primary-glow text-slate-900"
                >
                  Got it
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Footer />
      </div>
    );
  }

  // Logged in - show dashboard
  const loyaltyProgress = loyalty?.progress_to_free || 0;
  const totalServices = loyalty?.total_services || 0;
  const freeAvailable = loyalty?.free_services_available || 0;

  const copyReferralCode = () => {
    navigator.clipboard.writeText(user.referral_code || "");
    toast.success("Referral code copied!");
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="account-page">
      <Header />
      
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-2">
              Welcome, <span className="gradient-text">{user.name}</span>
            </h1>
            <p className="text-slate-400">{user.email}</p>
            {!user.email_verified && (
              <p className="text-yellow-400 text-sm mt-2 flex items-center justify-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Please check your email to verify your account
              </p>
            )}
          </motion.div>

          {/* Referral Program Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="glass rounded-2xl p-6 md:p-8 mb-8"
          >
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Gift className="w-6 h-6 text-cyan-400" />
              Refer a Friend
            </h2>
            
            <p className="text-slate-400 mb-4">
              Share your referral code! When they book and complete their first detail, you <strong className="text-cyan-400">BOTH get 15% off</strong> your next service.
            </p>

            <div className="bg-slate-800 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 mb-1">Your Referral Code</p>
                <p className="text-2xl font-bold text-cyan-400 tracking-wider">{user.referral_code || "Loading..."}</p>
              </div>
              <Button
                onClick={copyReferralCode}
                className="bg-slate-700 hover:bg-slate-600"
                data-testid="copy-referral-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>

            {user.referral_discount_available && (
              <div className="mt-4 bg-gradient-to-r from-yellow-400/20 to-orange-500/20 border border-yellow-400/30 rounded-xl p-4 text-center">
                <p className="text-yellow-400 font-bold">You have 15% off available from a referral!</p>
                <p className="text-slate-400 text-sm">It will be applied to your next booking</p>
              </div>
            )}
          </motion.div>

          {/* Loyalty Program Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-6 md:p-8 mb-8"
          >
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Award className="w-6 h-6 text-cyan-400" />
              Loyalty Rewards
            </h2>
            
            <div className="space-y-2 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-cyan-400/20 flex items-center justify-center">
                  <span className="text-cyan-400 font-bold text-sm">5</span>
                </div>
                <p className="text-slate-300">5 appointments = <span className="text-cyan-400 font-semibold">50% OFF</span> your next service</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-yellow-400/20 to-orange-500/20 flex items-center justify-center">
                  <Star className="w-4 h-4 text-yellow-400" />
                </div>
                <p className="text-slate-300">10 appointments = <span className="text-yellow-400 font-semibold">FREE</span> service!</p>
              </div>
            </div>

            {/* Car Journey Animation */}
            <div className="relative py-10">
              {/* Road Background */}
              <div className="absolute top-1/2 left-0 right-0 h-3 bg-slate-700 rounded-full -translate-y-1/2" />
              
              {/* Colored Progress Line */}
              <motion.div 
                className="absolute top-1/2 left-0 h-3 bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full -translate-y-1/2"
                initial={{ width: "0%" }}
                animate={{ width: `${Math.min(loyaltyProgress * 10, 100)}%` }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              />
              
              {/* Service Stops */}
              <div className="relative flex justify-between items-center px-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((stop) => {
                  const isCompleted = stop <= loyaltyProgress;
                  const isFree = stop === 10;
                  const isHalfOff = stop === 5;
                  const isCurrent = stop === loyaltyProgress;
                  return (
                    <div key={stop} className="flex flex-col items-center z-10 relative">
                      {/* Car on current position */}
                      {isCurrent && (
                        <motion.div
                          initial={{ y: 10, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          className="absolute -top-10"
                        >
                          <Car className="w-8 h-8 text-cyan-400" />
                        </motion.div>
                      )}
                      <div 
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-sm md:text-base font-bold transition-all ${
                          isCompleted 
                            ? "bg-cyan-400 text-slate-900 shadow-lg shadow-cyan-400/30" 
                            : isFree 
                              ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-slate-900"
                              : isHalfOff
                                ? "bg-cyan-400/30 text-cyan-400 border-2 border-cyan-400"
                                : "bg-slate-700 text-slate-400"
                        }`}
                      >
                        {isFree ? (
                          <Star className="w-5 h-5 md:w-6 md:h-6" />
                        ) : (
                          stop
                        )}
                      </div>
                      {isFree && (
                        <span className="text-xs text-yellow-400 mt-2 font-bold">FREE!</span>
                      )}
                      {isHalfOff && !isCompleted && (
                        <span className="text-xs text-cyan-400 mt-2 font-bold">50% OFF</span>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Car at start if no progress */}
              {loyaltyProgress === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute top-1/2 -translate-y-1/2 left-2 -mt-8 z-20"
                >
                  <Car className="w-8 h-8 text-slate-500" />
                </motion.div>
              )}
            </div>

            {/* Stats and Rewards */}
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <div className="bg-slate-800 rounded-xl px-4 py-3 text-center">
                <p className="text-2xl font-bold text-cyan-400">{totalServices}</p>
                <p className="text-xs text-slate-400">Total Services</p>
              </div>
              <div className="bg-slate-800 rounded-xl px-4 py-3 text-center">
                <p className="text-2xl font-bold text-cyan-400">{loyaltyProgress}/10</p>
                <p className="text-xs text-slate-400">Current Progress</p>
              </div>
            </div>

            {/* Available Rewards Section */}
            <div className="mt-6 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-medium mb-3 text-center">Your Available Rewards</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {loyaltyProgress >= 5 && loyaltyProgress < 10 && (
                  <div className="bg-cyan-500/20 border border-cyan-500/40 rounded-xl px-5 py-4 text-center">
                    <Award className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                    <p className="text-xl font-bold text-cyan-400">50% OFF</p>
                    <p className="text-xs text-cyan-400/80">Next service discount</p>
                    <p className="text-xs text-slate-500 mt-1">Use code: LOYAL50</p>
                  </div>
                )}
                {freeAvailable > 0 && (
                  <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/40 rounded-xl px-5 py-4 text-center">
                    <Star className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-xl font-bold text-yellow-400">{freeAvailable} FREE</p>
                    <p className="text-xs text-yellow-400/80">Service{freeAvailable > 1 ? 's' : ''} available</p>
                    <p className="text-xs text-slate-500 mt-1">Use code: FREESERVICE</p>
                  </div>
                )}
                {loyaltyProgress < 5 && freeAvailable === 0 && (
                  <div className="bg-slate-800 rounded-xl px-5 py-4 text-center">
                    <p className="text-slate-400">{5 - loyaltyProgress} more services until 50% off!</p>
                    <p className="text-slate-500 text-xs mt-1">Keep going - rewards are waiting!</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Bookings List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-2xl p-6 md:p-8"
          >
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Calendar className="w-6 h-6 text-cyan-400" />
              Your Bookings
            </h2>

            {bookings.length === 0 ? (
              <div className="text-center py-8">
                <Car className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 mb-4">No bookings yet</p>
                <Button 
                  onClick={() => navigate('/')}
                  className="btn-primary-glow text-slate-900"
                >
                  Book Your First Detail
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking, index) => (
                  <div 
                    key={booking.id}
                    className="bg-slate-800 rounded-xl p-4"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium">{booking.package?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            booking.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            booking.status === 'confirmed' ? 'bg-cyan-500/20 text-cyan-400' :
                            booking.status === 'in_progress' ? 'bg-purple-500/20 text-purple-400' :
                            booking.status === 'deposit_paid' ? 'bg-blue-500/20 text-blue-400' :
                            booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {booking.status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          {booking.status === 'deposit_paid' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                              Deposit Paid
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400">
                          {booking.preferred_date} at {booking.preferred_time}
                        </p>
                        <p className="text-sm text-slate-500">{booking.make_model}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-cyan-400">${booking.total_price}</p>
                        <p className="text-xs text-slate-500">{booking.vehicle_type}</p>
                      </div>
                    </div>
                    
                    {/* Pending booking request info */}
                    {booking.status === 'pending' && (
                      <div className="mt-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                        <p className="text-cyan-400 text-sm">
                          <AlertCircle className="w-4 h-4 inline mr-1" />
                          Request received! We'll contact you shortly to confirm your time and send payment details.
                        </p>
                      </div>
                    )}

                    {/* Pending deposit warning (legacy bookings) */}
                    {booking.status === 'pending_deposit' && (
                      <div className="mt-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                        <p className="text-yellow-400 text-sm">
                          <AlertCircle className="w-4 h-4 inline mr-1" />
                          Your appointment is not secured until the deposit is paid.
                        </p>
                      </div>
                    )}
                    
                    {/* Action buttons */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {booking.status === 'pending_deposit' && (
                        <Button
                          onClick={() => handlePayDeposit(booking)}
                          className="bg-cyan-600 hover:bg-cyan-700 text-sm"
                        >
                          Pay $20 Deposit
                        </Button>
                      )}
                      {/* Pay Balance button for services with outstanding balance */}
                      {['confirmed', 'completed', 'deposit_paid'].includes(booking.status) && getBalanceDue(booking) > 0 && (
                        <Button
                          onClick={() => handlePayBalance(booking)}
                          className="bg-green-600 hover:bg-green-700 text-sm"
                          data-testid={`pay-balance-${booking.id}`}
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Pay Balance (${getBalanceDue(booking).toFixed(2)})
                        </Button>
                      )}
                      {!['completed', 'cancelled'].includes(booking.status) && (
                        <Button
                          onClick={() => handleManageBooking(booking)}
                          className="bg-slate-700 hover:bg-slate-600 text-sm"
                        >
                          Manage Booking
                        </Button>
                      )}
                    </div>
                    
                    {/* Balance due indicator */}
                    {['confirmed', 'completed', 'deposit_paid'].includes(booking.status) && getBalanceDue(booking) > 0 && (
                      <div className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                        <p className="text-amber-400 text-sm flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          Balance due: <span className="font-bold">${getBalanceDue(booking).toFixed(2)}</span>
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Manage Booking Modal */}
          <AnimatePresence>
            {showManageModal && selectedBooking && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                onClick={() => setShowManageModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="glass rounded-2xl p-6 max-w-md w-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">Manage Booking</h2>
                    <button onClick={() => setShowManageModal(false)} className="text-slate-400 hover:text-white">
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="bg-slate-800 rounded-lg p-4 mb-4">
                    <p className="font-medium">{selectedBooking.package?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</p>
                    <p className="text-sm text-slate-400">{selectedBooking.preferred_date} at {selectedBooking.preferred_time}</p>
                    <p className="text-cyan-400 font-bold mt-2">${selectedBooking.total_price}</p>
                    {(selectedBooking.discount_type || selectedBooking.discount_code) && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                          {selectedBooking.discount_type || `Code: ${selectedBooking.discount_code}`}
                        </span>
                        {selectedBooking.discount_amount > 0 && (
                          <span className="text-xs text-green-400">-${selectedBooking.discount_amount.toFixed(2)} saved</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Button
                      onClick={() => { setShowManageModal(false); setShowReschedule(true); }}
                      className="w-full bg-cyan-600 hover:bg-cyan-700"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Reschedule
                    </Button>
                    <Button
                      onClick={() => setShowCancelConfirm(true)}
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel Booking
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Cancel Confirmation Modal */}
          <AnimatePresence>
            {showCancelConfirm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
                onClick={() => setShowCancelConfirm(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="glass rounded-2xl p-6 max-w-sm w-full text-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                  <h2 className="text-xl font-bold mb-2">Cancel Booking?</h2>
                  <p className="text-slate-400 mb-4">
                    Are you sure you want to cancel this booking?
                  </p>
                  <p className="text-yellow-400 text-sm mb-6 bg-yellow-400/10 p-3 rounded-lg">
                    <strong>Warning:</strong> Your $20 deposit will be forfeited if you continue to cancel.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowCancelConfirm(false)}
                      className="flex-1 bg-slate-700 hover:bg-slate-600"
                    >
                      No, Keep It
                    </Button>
                    <Button
                      onClick={handleCancelBooking}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      Yes, Cancel
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Reschedule Modal */}
          <AnimatePresence>
            {showReschedule && selectedBooking && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto"
                onClick={() => setShowReschedule(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="glass rounded-2xl p-6 max-w-md w-full my-8"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">Reschedule Booking</h2>
                    <button onClick={() => setShowReschedule(false)} className="text-slate-400 hover:text-white">
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                      <p className="text-yellow-400 text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Reschedule requests require confirmation. We'll notify you once approved.
                      </p>
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-2 block">New Date</Label>
                      <CalendarComponent
                        mode="single"
                        selected={rescheduleDate}
                        onSelect={setRescheduleDate}
                        disabled={(date) => date < new Date()}
                        className="rounded-lg border border-slate-700 bg-slate-800"
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-2 block">New Time</Label>
                      <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                        <SelectTrigger className="bg-slate-800 border-slate-700">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
                          {reschedule_times.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <p className="text-sm text-cyan-400 font-medium mb-3">Update details if needed:</p>
                      
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-slate-500">Phone</Label>
                          <Input
                            value={rescheduleDetails.phone}
                            onChange={(e) => setRescheduleDetails({...rescheduleDetails, phone: e.target.value})}
                            className="bg-slate-800 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Address</Label>
                          <Input
                            value={rescheduleDetails.address}
                            onChange={(e) => setRescheduleDetails({...rescheduleDetails, address: e.target.value})}
                            className="bg-slate-800 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Special Instructions</Label>
                          <Textarea
                            value={rescheduleDetails.special_instructions}
                            onChange={(e) => setRescheduleDetails({...rescheduleDetails, special_instructions: e.target.value})}
                            className="bg-slate-800 border-slate-700"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={handleReschedule}
                      className="w-full btn-primary-glow text-slate-900"
                    >
                      Confirm Reschedule
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Logout Button */}
          <div className="text-center mt-8">
            <button
              onClick={handleLogout}
              className="text-slate-400 hover:text-red-400 text-sm transition"
            >
              Sign Out
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal />
    </div>
  );
};

export default AccountPage;
